import { Box, Typography } from "@mui/material";
import type { PrincipleId, PrincipleStatus } from "@chessla/shared";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { lookupBookMove } from "@/features/openings";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { MotifBadge, PrincipleGlyph } from "@/shared/components";
import { layoutTokens } from "@/shared/theme";
import { boardEvidenceMotifAnnotation } from "./BoardMotifBadge";
import { buildGameDetailsEvidence, type EvidenceSide, type GameDetailsEvidenceRow } from "./game-details-evidence";

interface GameDetailsEvidenceMatrixProps {
  moves: readonly AnalyzedMoveResult[];
  playerColor: "w" | "b";
  opponent: string | null;
  children?: ReactNode;
}

const BADGE_SIZE = 36;
const GOLDEN_RATIO = 1.618;
const ROW_HEIGHT = Math.round(BADGE_SIZE * GOLDEN_RATIO);

const EvidenceCell = ({
  row,
  side,
  bookStatus,
}: {
  row: GameDetailsEvidenceRow;
  side: EvidenceSide;
  bookStatus: "loading" | "unavailable" | "ready";
}) => {
  const occurrences = row[side];
  const unavailable = row.id === "quality:book" && bookStatus !== "ready";
  return (
    <Box role="cell" sx={{ minWidth: 0, textAlign: "center", py: 0.5 }}>
      <Typography
        variant="body1"
        aria-label={unavailable ? `Book count ${bookStatus}` : undefined}
        title={unavailable ? `Book count ${bookStatus}` : undefined}
        sx={{
          color: occurrences.length ? row.color : "text.secondary",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 950,
          lineHeight: GOLDEN_RATIO,
        }}
      >
        {unavailable ? "?" : occurrences.length}
      </Typography>
    </Box>
  );
};

export const GameDetailsEvidenceMatrix = ({
  moves,
  playerColor,
  opponent,
  children,
}: GameDetailsEvidenceMatrixProps) => {
  const [bookLookup, setBookLookup] = useState<typeof lookupBookMove | null>();
  useEffect(() => {
    let active = true;
    void import("@/features/openings")
      .then(({ lookupBookMove }) => {
        if (active) setBookLookup(() => lookupBookMove);
      })
      .catch(() => {
        if (active) setBookLookup(null);
      });
    return () => {
      active = false;
    };
  }, []);
  const bookStatus = bookLookup === undefined ? "loading" : bookLookup === null ? "unavailable" : "ready";
  const sections = useMemo(() => buildGameDetailsEvidence(moves, bookLookup ?? null), [moves, bookLookup]);
  const playerSide: EvidenceSide = playerColor === "w" ? "white" : "black";
  const opponentSide: EvidenceSide = playerSide === "white" ? "black" : "white";
  const names = {
    white: playerColor === "w" ? "YOU" : (opponent ?? "OPPONENT"),
    black: playerColor === "b" ? "YOU" : (opponent ?? "OPPONENT"),
  };
  const columns = `minmax(0, 1fr) ${ROW_HEIGHT}px ${BADGE_SIZE}px ${ROW_HEIGHT}px minmax(0, 1fr)`;

  return (
    <Box sx={{ minWidth: 0 }}>
      {children}
      <Box
        role="table"
        aria-label="Game evidence summary"
        sx={{
          mt: children ? layoutTokens.contentGap : 0,
          pt: layoutTokens.panelPadding,
          borderBlockStart: `${layoutTokens.dividerBorder}px solid`,
          borderColor: "divider",
        }}
      >
        <Box
          role="row"
          sx={{ display: "grid", gridTemplateColumns: columns, alignItems: "end", pb: layoutTokens.controlGap }}
        >
          {[playerSide, opponentSide].map((side, index) => (
            <Box
              role="columnheader"
              key={side}
              sx={{
                minWidth: 0,
                textAlign: index === 0 ? "right" : "left",
                gridColumn: index === 0 ? "1 / 3" : "4 / -1",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  width: ROW_HEIGHT,
                  ml: index === 0 ? "auto" : 0,
                  textAlign: "center",
                  color: "text.secondary",
                  fontWeight: 900,
                  letterSpacing: ".1em",
                  lineHeight: GOLDEN_RATIO,
                }}
              >
                {side.toUpperCase()}
              </Typography>
              <Typography
                variant="body2"
                title={names[side]}
                sx={{
                  width: "100%",
                  color: "primary.main",
                  fontWeight: 950,
                  lineHeight: GOLDEN_RATIO,
                  overflowWrap: "anywhere",
                  whiteSpace: "normal",
                }}
              >
                {names[side]}
              </Typography>
            </Box>
          ))}
        </Box>

        {sections.map((section, sectionIndex) => (
          <Box role="rowgroup" aria-label={section.label} key={section.id}>
            <Typography
              variant="body2"
              component="h3"
              sx={{
                mt: sectionIndex === 0 ? 0 : layoutTokens.contentGap,
                py: layoutTokens.controlGap,
                borderBlockStart: `${layoutTokens.dividerBorder}px solid`,
                borderColor: "divider",
                color: "secondary.main",
                fontWeight: 950,
                letterSpacing: ".11em",
                lineHeight: GOLDEN_RATIO,
              }}
            >
              {section.label}
            </Typography>
            {section.rows.map((row) => (
              <Box
                role="row"
                key={row.id}
                sx={{ display: "grid", gridTemplateColumns: columns, alignItems: "center", minHeight: ROW_HEIGHT }}
              >
                <Typography
                  variant="body1"
                  role="rowheader"
                  sx={{ color: "primary.main", fontWeight: 800, lineHeight: GOLDEN_RATIO }}
                >
                  {row.label}
                </Typography>
                <EvidenceCell row={row} side={playerSide} bookStatus={bookStatus} />
                <Box
                  role="img"
                  aria-label={`${row.label} badge`}
                  title={`${row.label} badge`}
                  sx={{
                    width: BADGE_SIZE,
                    height: BADGE_SIZE,
                    justifySelf: "center",
                    "& [data-motif-badge]": { fontSize: { xs: ".76rem", sm: ".84rem" } },
                  }}
                >
                  {(() => {
                    const [group, status, principle] = row.id.split(":") as [
                      string,
                      PrincipleStatus,
                      PrincipleId | undefined,
                    ];
                    if (group === "principle" && principle) {
                      return (
                        <Box
                          data-principle-status={status}
                          sx={{
                            width: "100%",
                            height: "100%",
                            bgcolor: "#0a0a0a",
                            color: status === "applied" ? "primary.main" : "secondary.main",
                            border: "2px solid currentColor",
                            boxShadow: "2px 2px 0 #0a0a0a",
                            boxSizing: "border-box",
                            display: "grid",
                            placeItems: "center",
                          }}
                        >
                          <PrincipleGlyph principle={principle} />
                        </Box>
                      );
                    }

                    const motif = row.id.slice(row.id.indexOf(":") + 1);
                    const presentation = boardEvidenceMotifAnnotation([motif]);
                    return (
                      <MotifBadge
                        kind={presentation?.kind ?? "text"}
                        label={presentation?.label ?? row.glyph}
                        color={presentation?.color ?? row.color}
                      />
                    );
                  })()}
                </Box>
                <EvidenceCell row={row} side={opponentSide} bookStatus={bookStatus} />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
